/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */


import * as $ from "jquery";
import {UiField} from "./UiField";
import {SubEventFactory} from "../../generated/SubEventFactory";
import {TeamAppsUiContext} from "../TeamAppsUiContext";
import {UiLabel_ClickedSubEvent, UiLabelConfig, UiLabelSubCommandHandler, UiLabelSubEventSource} from "../../generated/UiLabelConfig";
import {TeamAppsEvent} from "../util/TeamAppsEvent";
import {UiFieldEditingMode} from "../../generated/UiFieldEditingMode";
import {TeamAppsUiComponentRegistry} from "../TeamAppsUiComponentRegistry";

/**
 * @author Yann Massard (yamass@gmail.com)
 */
export class UiLabel extends UiField<UiLabelConfig, string> implements UiLabelSubEventSource, UiLabelSubCommandHandler {
	public readonly onClicked: TeamAppsEvent<UiLabel_ClickedSubEvent> = new TeamAppsEvent<UiLabel_ClickedSubEvent>(this);

	private $main: JQuery;
	private $caption: JQuery;

	protected initialize(config: UiLabelConfig, targetElement: Element, context: TeamAppsUiContext): void {
		this.$main = $(`<div class="UiLabel"><span class="caption">${config.caption}</span></div>`);
		this.$caption = this.$main.find(".caption");
		this.setIcon(config.icon);
		this.$main.click(() => {
			this.onClicked.fire(SubEventFactory.createUiLabel_ClickedSubEvent());
		});
	}

	isValidData(v: string): boolean {
		return v == null || typeof v === "string";
	}

	public get targetFieldName() {
		return this._config.targetFieldName;
	}

	setCaption(caption: string): void {
		this.$caption.text(caption);
	}

	setIcon(icon: string): void {
		this.$main.find(".icon").remove();
		if (icon) {
			let iconPath = this._context.getIconPath(icon, 16);
			this.$main.prepend(`<div class="icon img img-16" style="background-image: url(${iconPath})"></div>`)
		}
	}

	public getMainDomElement(): JQuery {
		return this.$main;
	}

	public getFocusableElement(): JQuery {
		return null;
	}

	public destroy(): void {
		// nothing to do.
	}

	public getTransientValue(): string {
		return this.getCommittedValue();
	}

	public getDefaultValue(): string {
		return null;
	}

	protected displayCommittedValue(): void {
		this.$caption.text(this.getCommittedValue() || this._config.caption || "");
	}

	protected onEditingModeChanged(editingMode: UiFieldEditingMode): void {
		// does not have any editing mode support...
	}

	public valuesChanged(v1: string, v2: string): boolean {
		return v1 !== v2;
	}
}

TeamAppsUiComponentRegistry.registerFieldClass("UiLabel", UiLabel);